import React, {useState, useEffect} from 'react';
import QCStatusToggle from '@pluto/qc-status-toggle/QCStatusToggle';
import {useParams} from 'react-router-dom';
import wrap from 'wraptile';
import {
    NoteType,
    OnInputChangeEvent,
    OnSelectChangeEvent,
    UpdateQcJobStatusParams,
    QcJobStatuses,
} from '@pluto/@types';
import {curry} from 'ramda';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import {CreateNewQcReportNoteParams, QCReportsNotesState} from './QCReportsReasonsTypes';
import QCReportsReasons from './qc-reports-reasons/QCReportsReasonsConnected';
import QCReportsNotes from './qc-reports-notes/QCReportsNotesConnected';
import {QcJobStatusIndicatorStateItem} from '../../QCTools/JobStatusIndicator/JobStatusIndicatorTypes';
import './QCReports.scss';

type CreateNewQcReportNoteType = (params: CreateNewQcReportNoteParams) => void;
type UpdateQcJobStatusType = (qcJobId: string, params: UpdateQcJobStatusParams) => void;
type SetReasonLabelType = (value: string) => void;
type SetReasonType = (value: number | string) => void;

export interface QCReportsContainerProps {
    qcReportsNotes: QCReportsNotesState;
    createNewQcReportNote: CreateNewQcReportNoteType;
    updateQcJobReportStatus: UpdateQcJobStatusType;
    noteType: NoteType;
    selectReasonPlaceholder: string;
    statusIndicators: QcJobStatusIndicatorStateItem[];
    jobDeliveryDate?: string;
}

export const getNewStatus = (newStatus: QcJobStatuses | null) => {
    return newStatus || QcJobStatuses.Pending;
};

export const handleStatusChange = curry(
    (
        setReportStatus: (value: QcJobStatuses) => void,
        updateQcJobReportStatus: UpdateQcJobStatusType,
        qcJobId: string,
        noteType: NoteType,
        qcReportsNotes: QCReportsNotesState,
        event: React.MouseEvent<HTMLElement>,
        newStatus: QcJobStatuses
    ) => {
        const {data} = qcReportsNotes;

        if (!data.length && newStatus === QcJobStatuses.Failed) {
            return updateQcJobReportStatus(qcJobId, {
                type: noteType,
                status: newStatus as QcJobStatuses,
            });
        }
        setReportStatus(newStatus);
        updateQcJobReportStatus(qcJobId, {
            type: noteType,
            status: getNewStatus(newStatus as QcJobStatuses),
        });
    }
);

export const handleLabelChange = curry(
    (setReasonLabel: SetReasonLabelType, e: OnInputChangeEvent) => {
        setReasonLabel(e.target.value);
    }
);

export const handleReasonChange = curry(
    (setReason: SetReasonType, e: OnSelectChangeEvent) => {
        setReason(e.target.value as number);
    }
);

export const handleCreatingNewNote = wrap(
    (
        createNewQcReportNote: (
            params: CreateNewQcReportNoteParams,
            noteType: NoteType
        ) => void,
        setReasonLabel: SetReasonLabelType,
        setReason: SetReasonType,
        params: CreateNewQcReportNoteParams,
        noteType: NoteType
    ) => {
        createNewQcReportNote(params, noteType);
        setReasonLabel('');
        setReason('');
    }
);

export const isButtonDisabled = (reasonLabel: string, reason: number | string) => {
    return !reasonLabel || !reason;
};

export const getInitialReportStatus = (
    statusIndicators: QcJobStatusIndicatorStateItem[],
    noteType: NoteType
) => {
    return statusIndicators.find((status) => status.type === noteType)?.status;
};

export const updateReportStatus = wrap(
    (
        initialReportStatus: QcJobStatuses,
        setReportStatus: (value: QcJobStatuses) => void
    ) => {
        setReportStatus(initialReportStatus);
    }
);

const QCReportsContainer = (props: QCReportsContainerProps) => {
    const {
        qcReportsNotes,
        createNewQcReportNote,
        updateQcJobReportStatus,
        noteType,
        selectReasonPlaceholder,
        statusIndicators,
        jobDeliveryDate,
    } = props;
    const initialReportStatus = getInitialReportStatus(statusIndicators, noteType);
    const {id} = useParams<{id: string}>();
    const [reportStatus, setReportStatus] = useState<QcJobStatuses>(
        initialReportStatus as QcJobStatuses
    );
    const [reasonLabel, setReasonLabel] = useState<string>('');
    const [reason, setReason] = useState<number | string>('');
    const onEffect = updateReportStatus(initialReportStatus, setReportStatus);

    useEffect(onEffect, [statusIndicators]);

    const onChangeToggle = handleStatusChange(
        setReportStatus,
        updateQcJobReportStatus,
        id,
        noteType,
        qcReportsNotes
    );
    const onChangeLabel = handleLabelChange(setReasonLabel);
    const onChangeReason = handleReasonChange(setReason);
    const newNoteParams = {
        id,
        body: {
            timespanCategoryId: reason,
            value: reasonLabel,
        },
    };

    return (
        <div className="qc-reports">
            <div className="qc-reports__wrapper">
                <div className="qc-reports__header">QC report</div>
                <div className="qc-reports__status">
                    {!jobDeliveryDate ? (
                        <div className="qc-reports__status-toggle">
                            <QCStatusToggle
                                status={reportStatus}
                                onStatusChange={onChangeToggle}
                            />
                        </div>
                    ) : (
                        <div className="qc-reports__status-passed-value">QC Passed</div>
                    )}
                    <div className="qc-reports__reason-selector">
                        <QCReportsReasons
                            selectReasonPlaceholder={selectReasonPlaceholder}
                            value={reason}
                            noteType={noteType}
                            onChange={onChangeReason}
                            disabled={Boolean(jobDeliveryDate)}
                        />
                    </div>
                    <div className="qc-reports__add-reason">
                        <TextField
                            className="qc-reports__reason-label"
                            multiline
                            rows="4"
                            inputProps={{
                                maxLength: 1024,
                            }}
                            variant="filled"
                            value={reasonLabel}
                            onChange={onChangeLabel}
                            placeholder="Description"
                            disabled={Boolean(jobDeliveryDate)}
                        />
                    </div>
                    <div className="qc-reports__add-note">
                        <Button
                            disabled={isButtonDisabled(reasonLabel, reason)}
                            variant="outlined"
                            onClick={handleCreatingNewNote(
                                createNewQcReportNote,
                                setReasonLabel,
                                setReason,
                                newNoteParams,
                                noteType
                            )}
                        >
                            Add Note
                        </Button>
                    </div>
                </div>
                <div className="qc-reports__notes">
                    <QCReportsNotes
                        jobDeliveryDate={jobDeliveryDate}
                        noteType={noteType}
                    />
                </div>
            </div>
        </div>
    );
};

export default QCReportsContainer;
